import { useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { FormProvider, SubmitHandler, useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";

import { Button } from "@pentabd/ui";

import { ErrorModal, SuccessModal, CancelButton } from "@components";
import { BasicInfo } from "./BasicInfo";
// import { RichTextEditor } from "./RichTextEditor";
import { SupportingDocument } from "./SupportingDocuments";

import { yupResolver } from "@hookform/resolvers/yup";
import { useGeneralInfo } from "@hooks/HRM/employee/useGeneralInfo";
import { useResignation } from "@hooks/HRM/resignation/useResignation";

import { ROUTES } from "@constants/routes";

import { GeneralInfoType } from "@type/HRM/general-info";

import {
  ResignationFormDataType,
  SUPPORTING_DOCUMENT,
  resignationFormValidation,
} from "@validations/HRM/resignation/resignationFormValidation";
import { convertDocumentData } from "@utils/documentDataConvert";

const {
  HRM: { RESIGNATION },
} = ROUTES;

export const CreateResignationBody = () => {
  const [isSuccessModalOpen, setIsSuccessModalOpen] = useState(false);
  const [isErrorModalOpen, setIsErrorModalOpen] = useState(false);

  const { t } = useTranslation();
  const navigate = useNavigate();
  const { id } = useParams();

  const {
    createResignationSuccess,
    createResignationFailed,
    createResignation,
  } = useResignation();

  const { generalInfo } = useGeneralInfo(id as any);
  const { id: employeeMasterId, unitPositionId } = generalInfo || {};

  const methods = useForm<ResignationFormDataType>({
    resolver: yupResolver(resignationFormValidation),
    values: generalInfo as GeneralInfoType,
  });
  const { handleSubmit } = methods;

  const onSubmit: SubmitHandler<ResignationFormDataType> = (data: any) => {
    createResignation({
      employeeMasterId,
      unitPositionId,
      ...convertDocumentData(
        data,
        SUPPORTING_DOCUMENT.EMPLOYEE_SUPPORTING_DOCUMENT,
      ),
    });
  };

  const closeErrorModal = () => setIsErrorModalOpen(false);
  const createSuccessAction = () => navigate(RESIGNATION);

  useEffect(() => {
    if (createResignationSuccess) setIsSuccessModalOpen(true);
    else if (createResignationFailed) setIsErrorModalOpen(true);
  }, [createResignationSuccess, createResignationFailed]);

  useEffect(() => {
    const reset = () => {
      setIsErrorModalOpen(false);
      setIsSuccessModalOpen(false);
    };
    return reset();
  }, []);

  return (
    <>
      {/* MODAL SECTION */}
      <>
        {isSuccessModalOpen && (
          <SuccessModal
            isModalOpen={isSuccessModalOpen}
            action={createSuccessAction}
            subtitle="Application initiated successfully"
          />
        )}
        {isErrorModalOpen && (
          <ErrorModal
            isModalOpen={isErrorModalOpen}
            closeModal={closeErrorModal}
          />
        )}
      </>
      {/* MODAL SECTION */}
      <FormProvider {...methods}>
        <form className="container" onSubmit={handleSubmit(onSubmit)}>
          <BasicInfo />
          {/* <RichTextEditor /> */}
          <SupportingDocument />
          <div className="w-100 d-flex justify-content-end gap-6 border-top mt-8 pt-8">
            <CancelButton />
            <Button fill="fill" size="sm" type="primary" htmlType="submit">
              {t("RESIGNATION.FORM.SAVE_BUTTON")}
            </Button>
          </div>
        </form>
      </FormProvider>
    </>
  );
};
